import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"

import { updateChannelOrientation } from "@/api"
import { categories } from "@/components/shared/global/pop-up-orientation.constants"
import { TourGuide } from "@/components/shared/global/tour-guide"
import { Checkbox } from "@/components/ui"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent } from "@/components/ui/sheet"
import { EKocCategory } from "@/modules/helpers/enum"
import { useCurrentUser, useLayoutStore } from "@/modules/helpers/hooks/shared"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import { sendHaptic } from "@/modules/helpers/utils"

type SelectionItemProps = {
  title: string
  description: string
  selected: boolean
  onSelect: () => void
  disabled?: boolean
}

const SelectionItem = ({
  title,
  description,
  selected,
  onSelect,
  disabled
}: SelectionItemProps) => {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center w-full p-4 rounded-2xl transition-all cursor-pointer bg-background",
        disabled && "opacity-50 cursor-not-allowed"
      )}
      onClick={(e) => {
        e.stopPropagation()
        if (!disabled) {
          onSelect()
        }
      }}
    >
      <div className="flex items-center justify-between w-full gap-3 mb-2">
        <h3 className="text-sm font-semibold text-primary-800 text-left flex-1">{title}</h3>
        <Checkbox
          checked={selected}
          disabled={disabled}
          className="data-[state=checked]:bg-primary-800 data-[state=checked]:text-white"
        />
      </div>
      <p className="text-xs text-primary-800/90 text-left w-full leading-relaxed">{description}</p>
    </div>
  )
}

interface OrientationForm {
  selectedCategories: EKocCategory[]
}

const PopUpOrientation = ({
  isVisible,
  setIsVisible
}: {
  isVisible?: boolean
  setIsVisible?: (isVisible: boolean) => void
}) => {
  const [shouldShow, setShouldShow] = useState(false)
  const { user } = useCurrentUser()
  const queryClient = useQueryClient()
  const { insetBottom, insetTop } = useLayoutStore()
  const form = useForm<OrientationForm>({
    defaultValues: {
      selectedCategories: []
    }
  })

  // Sync selectedCategories with user data when user changes
  useEffect(() => {
    if (user?.channelOrientation) {
      form.reset({ selectedCategories: user.channelOrientation })
    } else {
      form.reset({ selectedCategories: [] })
    }
  }, [user?.channelOrientation, form])

  // Show popup only if user doesn't have channelOrientation set
  useEffect(() => {
    setShouldShow(!user?.channelOrientation?.length)
  }, [user?.channelOrientation])

  const selectedCategories = form.watch("selectedCategories")

  const updateOrientationMutation = useMutation({
    mutationFn: (channelOrientation: EKocCategory[]) =>
      updateChannelOrientation(channelOrientation),
    onSuccess: () => {
      // Invalidate user query to refetch
      queryClient.invalidateQueries({ queryKey: ["user_info"] })
      toast.success("Cập nhật định hướng KOC thành công!")
      sendHaptic()
      setIsVisible?.(false)
      setShouldShow(false)
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Có lỗi xảy ra. Vui lòng thử lại.")
      sendHaptic()
      // Không đóng popup để user có thể thử lại
    }
  })

  const handleToggleCategory = (category: EKocCategory) => {
    sendHaptic()
    const current = form.getValues("selectedCategories")
    if (current.includes(category)) {
      // Remove category
      form.setValue(
        "selectedCategories",
        current.filter((c) => c !== category)
      )
    } else {
      // Add category (max 3)
      if (current.length >= 3) {
        toast.warning("Bạn chỉ có thể chọn tối đa 3 định hướng")
        return
      }
      form.setValue("selectedCategories", [...current, category])
    }
  }

  const handleSubmit = (data: OrientationForm) => {
    if (data.selectedCategories.length === 0) {
      toast.warning("Vui lòng chọn ít nhất 1 định hướng")
      return
    }
    updateOrientationMutation.mutate(data.selectedCategories)
  }

  if ((!shouldShow && !isVisible) || !user) {
    return <TourGuide />
  }

  return (
    <Sheet open={shouldShow || isVisible} modal>
      <SheetContent
        className="w-full"
        side="right"
        hiddenClose
        style={{
          paddingBottom: insetBottom,
          paddingTop: insetTop
        }}
      >
        <div className="h-full w-full flex flex-col">
          <div className="flex flex-col h-full w-full p-4 py-6">
            {/* Header */}
            <div className="mb-4">
              <h2 className="text-lg font-bold text-black mb-2">Định hướng KOC</h2>
              <p className="text-xs text-black/80 pr-8">
                Hãy lựa chọn định hướng mà kênh của bạn đang theo đuổi.
              </p>
              <p className="text-xs text-black/80 pr-8">(Chọn tối đa 3 định hướng)</p>
            </div>

            {/* Categories List - Scrollable */}
            <div className="flex-1 overflow-y-auto mb-4 space-y-3 pr-2">
              {categories.map((category) => {
                const isSelected = selectedCategories.includes(category.value)
                const isDisabled = !isSelected && selectedCategories.length >= 3
                return (
                  <SelectionItem
                    key={category.value}
                    title={category.title}
                    description={category.description}
                    selected={isSelected}
                    onSelect={() => handleToggleCategory(category.value)}
                    disabled={isDisabled}
                  />
                )
              })}
            </div>

            {/* Submit Button */}
            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <Button
                type="submit"
                disabled={selectedCategories.length === 0 || updateOrientationMutation.isPending}
                className="w-full bg-gradient-primary rounded-xl text-white hover:bg-gradient-primary/90 font-semibold py-6 text-sm"
              >
                {updateOrientationMutation.isPending ? "Đang lưu..." : "Lưu"}
              </Button>
            </form>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

export { PopUpOrientation }
